import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Text } from "@volca/ui/components/ui/text";
import { useAuth } from "@/core/auth";
import { BUSINESS_PARAM, useBusiness } from "@/core/business";
import {
  canEditPayrollRoster,
  useCanViewPayrollRoster,
} from "@/payroll/roster/permissions";
import { usePayrollRoster } from "@/payroll/roster/query";
import { getPayrollRosterSuggestions } from "@/payroll/roster/utils";
import { PAYROLL_SETTINGS_ROSTER_PATH } from "@/payroll/settings/routes";

export const PayrollRosterWarning = ({
  businessId,
}: {
  businessId: string;
}) => {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const { accessQuery, canViewRoster } = useCanViewPayrollRoster(
    selectedBusiness,
    user,
  );
  const canManage =
    canViewRoster &&
    canEditPayrollRoster(selectedBusiness, user, accessQuery.data);
  const query = usePayrollRoster(canManage ? businessId : null);
  const roster = query.data;

  if (!canManage) return null;

  if (
    roster &&
    !canEditPayrollRoster(selectedBusiness, user, accessQuery.data, roster)
  )
    return null;

  const count = roster ? getPayrollRosterSuggestions(roster).length : 0;

  if (!query.isError && count === 0) return null;

  const reviewParams = new URLSearchParams({
    [BUSINESS_PARAM]: businessId,
    focus: "missing",
  });

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            color="danger"
            size="xs"
            aria-label={
              query.isError
                ? "Payroll roster check unavailable"
                : `Review ${count} active ServiceTitan people not on the payroll roster`
            }
          />
        }
      >
        <WarningCircleIcon weight="fill" data-icon="inline-start" />
        <span className="tabular-nums">
          {query.isError ? "Roster check" : count}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 gap-3 p-4">
        <PopoverHeader>
          <PopoverTitle>
            {query.isError
              ? "Roster check unavailable"
              : `${count} ${count === 1 ? "person needs" : "people need"} a payroll decision`}
          </PopoverTitle>
          <PopoverDescription>
            {query.isError
              ? "Active ServiceTitan people could not be checked against the payroll roster."
              : "These people were found as active in ServiceTitan during sync, but have not yet been added to the payroll roster."}
          </PopoverDescription>
        </PopoverHeader>
        {query.isError ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        ) : (
          <>
            <Text>
              Add anyone who should be paid. For anyone who should not be on
              payroll, select <strong>X</strong> beside their suggestion to mark
              them as not on payroll and stop suggesting them.
            </Text>
            <Text variant="caption" tone="muted">
              Review and publish your roster changes when finished.
            </Text>
            <Button
              variant="secondary"
              size="sm"
              nativeButton={false}
              render={
                <Link
                  to={{
                    pathname: PAYROLL_SETTINGS_ROSTER_PATH,
                    search: reviewParams.toString(),
                  }}
                />
              }
            >
              Review people
              <ArrowRightIcon data-icon="inline-end" />
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
